import React, { useEffect, useState } from "react";
import { sortBy } from "lodash";

import { Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { HelpText, CategoryCard } from "@/components/atoms";
import { useInsightFilters } from "@/context";
import { InsightResult, ScoreType } from "@/types";
import { filterCategories } from "@/utils";

interface InterestsViewProps {
  scores: InsightResult[];
}

export const InterestsView: React.FC<InterestsViewProps> = ({ scores }) => {
  const {
    filters: { scoreType },
  } = useInsightFilters();

  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(
    undefined,
  );
  const [interests, setInterests] = useState<InsightResult[]>([]);

  const groups = sortBy(
    scores.filter((x) => x.tag_name === x.tag_category),
    scoreType.value,
  )
    .reverse()
    .map((x) => x.tag_category);

  const maxGroupScore = Math.max.apply(
    Math,
    scores
      .filter((x) => x.tag_name === x.tag_category)
      .map((x) => Number(x[scoreType.value as keyof InsightResult])),
  );
  const minGroupScore =
    scoreType.value === "rate"
      ? Math.min.apply(
          Math,
          scores
            .filter((x) => x.tag_name === x.tag_category)
            .map((x) => Number(x[scoreType.value as keyof InsightResult])),
        )
      : Math.max(1 - (maxGroupScore - 1), 0);

  const maxScore = Math.max.apply(
    Math,
    scores
      .filter((x) => x.tag_name !== x.tag_category)
      .map((x) => Number(x[scoreType.value as keyof InsightResult])),
  );
  const minScore =
    scoreType.value === "rate"
      ? Math.min.apply(
          Math,
          scores
            .filter((x) => x.tag_name !== x.tag_category)
            .map((x) => Number(x[scoreType.value as keyof InsightResult])),
        )
      : Math.max(1 - (maxScore - 1), 0);

  useEffect(() => {
    if (selectedGroup) {
      setInterests(
        filterCategories(scores, selectedGroup, selectedGroup, scoreType.value),
      );
    } else {
      setInterests([]);
    }
  }, [scores, selectedGroup, scoreType]);

  const handleGroupSelect = (group: string) => {
    setSelectedGroup((prevState) => (prevState !== group ? group : undefined));
  };

  return (
    <>
      {/* Group View */}
      <Grid size={12}>
        <HelpText
          text="Interest Groups"
          helpText="Select an Interest Group to show it's Sub-Interests"
        />
      </Grid>
      <Grid container size={12} spacing={2}>
        {groups.map((group, index) => {
          const groupScore = scores
            .filter((x) => x.tag_category === group)
            .find((x) => x.tag_name === x.tag_category);
          if (groupScore) {
            return (
              <CategoryCard
                key={`spend-category-${index}`}
                category={groupScore.tag_name}
                value={Number(
                  groupScore[scoreType.value as keyof InsightResult],
                )}
                score={scoreType.value}
                type="interest"
                minValue={minGroupScore}
                maxValue={maxGroupScore}
                selectedCategory={selectedGroup}
                onSelect={handleGroupSelect}
              />
            );
          } else {
            return null;
          }
        })}

        {/* Interest View */}
        {selectedGroup && (
          <>
            <Grid size={12}>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                <Typography>
                  {selectedGroup ? selectedGroup : "Top Scoring"}
                </Typography>
                <Typography>Interests</Typography>
              </Stack>
            </Grid>
            {interests.map((interest, index) => (
              <CategoryCard
                key={`spend-category-${index}`}
                category={interest.tag_name}
                value={Number(interest[scoreType.value as keyof InsightResult])}
                score={scoreType.value}
                type="interest"
                minValue={minScore}
                maxValue={maxScore}
              />
            ))}
          </>
        )}
      </Grid>
    </>
  );
};

export default InterestsView;
